import {
  Button,
  IconButton,
  LinkIcon,
  LoadingDarkIcon,
  RefreshIcon,
  palette,
} from "@minervaproject/fds";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { OpenInNewTab } from "./controls/open_in_new_tab";
import { ResourceSettingsMenu } from "./controls/resource_settings_menu";
import { ResourceTitle } from "./controls/resource_title";

import { CenteredErrorBoundary, TooltipErrorBoundary } from "components/error_boundary";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapabilities } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { useUserProperties } from "hooks/use_user_properties";
import { setResourcePresentationViewScrollPosition } from "shared/reducers/classroom";
import { LocalModalContentType, ResourceType, ResourceTypeType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";

// Some extra pixels of buffer prevent a second vertical scrollbar inside the Minerva Resource from appearing, which
// could occur if the horizontal scrollbar is present in that resource.
const IFRAME_HEIGHT_BUFFER_PIXELS = 30;
const IFRAME_DEFAULT_HEIGHT = "100%";
const MINERVA_RESOURCE_DOCUMENT_RESIZE_MESSAGE = "SLED.documentScrollHeight.resize";
const MINERVA_RESOURCE_WINDOW_DID_SCROLL_MESSAGE = "SLED.windowScroll.didScroll";
const MINERVA_RESOURCE_WINDOW_SHOULD_SCROLL_MESSAGE = "SLED.windowScroll.shouldScroll";

function MinervaResource({ resource }: { resource: ResourceType }) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [resourceUrl, setResourceUrl] = useState("");
  const isMobile = useIsMobile();
  const [iframeHeight, setIframeHeight] = useState(IFRAME_DEFAULT_HEIGHT);
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const { canControlClassroom, canParticipateInClass, isVisibleInClass } = useCapabilities();
  const studentsCanDraw = useAppSelector(classroomSelectors.selectStudentsCanDraw());
  const readOnly = (canParticipateInClass && !studentsCanDraw) || !isVisibleInClass;
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const { is_recording_agent } = useUserProperties(currentUserId, ["is_recording_agent"]) || {};
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const preclassResourceId = useAppSelector(classroomSelectors.selectPreclassResourceId());
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const localModalContent = useAppSelector(localSelectors.selectLocalModalContent());
  const noPresence =
    isPresentationView || (canControlClassroom && !!activeBreakoutSessionId) || is_recording_agent;
  // iframed also controls whether avatars are shown or not (iframed = true means no avatars)
  // for in-person breakouts we do want avatars to be visible
  const iframed = !(isInPerson && !!activeBreakoutSessionId);

  if (
    isPresentationView &&
    resource.presentation_view &&
    typeof resource.presentation_view[currentUserId]?.scroll_x !== "undefined" &&
    typeof resource.presentation_view[currentUserId]?.scroll_y !== "undefined"
  ) {
    iframeRef?.current?.contentWindow?.postMessage(
      {
        type: MINERVA_RESOURCE_WINDOW_SHOULD_SCROLL_MESSAGE,
        windowScrollX: resource.presentation_view[currentUserId].scroll_x,
        windowScrollY: resource.presentation_view[currentUserId].scroll_y,
      },
      "*",
    );
  }

  const getResourceUrl = useCallback(async () => {
    let url;
    if (resource.id === preclassResourceId) {
      url = await forumAPI.getPreclassResourceRollupSignedUrl(classId);
      // Hide rollup controls for presentation view
      if (isPresentationView) {
        url = url.replace("&isInstructor=1", "");
      }
    } else if (resource.type === ResourceTypeType.ForumDoc) {
      url = await forumAPI.getForumDocSignedUrl(resource.id, {
        iframed,
        noPresence,
        readOnly,
      });
    } else if (resource.type === ResourceTypeType.ForumWorkbook) {
      url = await forumAPI.getForumWorkbookSignedUrl(resource.id, {
        iframed,
        noPresence,
        readOnly,
      });
    }
    setResourceUrl(url);
  }, [
    classId,
    forumAPI,
    iframed,
    isPresentationView,
    readOnly,
    noPresence,
    preclassResourceId,
    resource.id,
    resource.type,
  ]);

  useEffect(() => {
    getResourceUrl();
  }, [getResourceUrl]);

  // On mobile, we need to specify the exact height as the minerva resource should scroll with some additional
  // content on the page. On a desktop interface, we do not need this behavior and the iframed resource will only
  // scroll within the resource itself.
  const minervaResourceResized = useCallback(
    (message: { data: { type: string; documentScrollHeight: number } }) => {
      if (isMobile && message.data.type === MINERVA_RESOURCE_DOCUMENT_RESIZE_MESSAGE) {
        setIframeHeight(`${message.data.documentScrollHeight + IFRAME_HEIGHT_BUFFER_PIXELS}px`);
      }
    },
    [isMobile],
  );

  // This is to track the instructor's scroll position if they are also using the presentation view.
  // We want any time the instructor scrolls on their main instructor display to be mimicked on the document
  // in the presentation view.
  const minervaResourceScrolled = useCallback(
    (message: {
      data: { type: string; documentId: string; windowScrollX: number; windowScrollY: number };
    }) => {
      if (
        message.data.type === MINERVA_RESOURCE_WINDOW_DID_SCROLL_MESSAGE &&
        resource.external_id === message.data.documentId
      ) {
        // If the presentation view scrolls, we want to ensure we clear out the scroll position in the resource and
        // we assume the instructor is manually handling the scrolling at this point.
        if (isPresentationView) {
          dispatch(
            setResourcePresentationViewScrollPosition({
              resourceId: resource.id,
              userId: currentUserId,
              scrollX: undefined,
              scrollY: undefined,
            }),
          );
        } else {
          dispatch(
            setResourcePresentationViewScrollPosition({
              resourceId: resource.id,
              userId: currentUserId,
              scrollX: message.data.windowScrollX,
              scrollY: message.data.windowScrollY,
            }),
          );
        }
      }
    },
    [resource.external_id, resource.id, isPresentationView, dispatch, currentUserId],
  );

  useEffect(() => {
    if (canControlClassroom) {
      window.addEventListener("message", minervaResourceScrolled);
    } else {
      window.removeEventListener("message", minervaResourceScrolled);
    }
    return () => {
      window.removeEventListener("message", minervaResourceScrolled);
    };
  }, [isPresentationView, canControlClassroom, minervaResourceScrolled]);

  useEffect(() => {
    if (isMobile) {
      window.addEventListener("message", minervaResourceResized);
    } else {
      window.removeEventListener("message", minervaResourceResized);
      setIframeHeight(IFRAME_DEFAULT_HEIGHT);
    }
    return () => {
      window.removeEventListener("message", minervaResourceResized);
    };
  }, [isMobile, minervaResourceResized]);

  const handleReload = () => {
    if (iframeRef.current && resourceUrl) {
      iframeRef.current.src = resourceUrl;
    } else {
      getResourceUrl();
    }
  };

  const handleOpenInNewTab = async () => {
    let url;
    if (resource.type === ResourceTypeType.ForumDoc) {
      url = await forumAPI.getForumDocSignedUrl(resource.id, { readOnly });
    } else if (resource.type === ResourceTypeType.ForumWorkbook) {
      url = await forumAPI.getForumWorkbookSignedUrl(resource.id, { readOnly });
    }
    window.open(url, "_blank");
    window.focus();
  };

  const handleBlur = () => {
    // Send a message to all iFrames that they have been blurred. SLEDs use this
    // to remove focus from the RichTextField, to avoid it from stealing focus when
    // it updates due to other users' actions
    const iframeEls = document.querySelectorAll("iframe");
    iframeEls.forEach((iframeEl) => {
      iframeEl.contentWindow?.postMessage("focus_state_controller:iframe-blurred", "*");
    });
  };

  useEffect(() => {
    // If a modal has been opened, prevent the iframe from stealing focus
    if (localModalContent !== LocalModalContentType.None) {
      handleBlur();
    }
  }, [localModalContent]);

  return (
    <div className="flex flex-column w-full h-full">
      {isMobile && !isPresentationView ? (
        <div className="bg-white pt2 pr5 pl5 pb1">
          <Button
            kind="icon-text-secondary-mobile"
            onClick={() => {
              navigator.clipboard.writeText(resourceUrl);
            }}
          >
            <LinkIcon color={palette.blueShade20} />
            Copy Link
          </Button>
        </div>
      ) : (
        <div className="flex items-center p1 bg-black body-s text-black-tint-70 resource-controls">
          <ResourceTitle resource={resource} />
          {!isPresentationView && (
            <>
              <TooltipErrorBoundary text="The resource settings menu cannot be displayed">
                <ResourceSettingsMenu editVerb="Edit" />
              </TooltipErrorBoundary>
              <TooltipErrorBoundary text="The refresh button cannot be displayed">
                <IconButton onClick={handleReload}>
                  <RefreshIcon width={16} height={16} color="var(--black-tint-70)" />
                </IconButton>
              </TooltipErrorBoundary>
              <TooltipErrorBoundary text="The open-in-a-new-tab button cannot be displayed">
                <OpenInNewTab handleOpenInNewTab={handleOpenInNewTab} />
              </TooltipErrorBoundary>
            </>
          )}
        </div>
      )}

      {resourceUrl ? (
        <CenteredErrorBoundary text="The resource cannot be displayed">
          <div className="bg-white flex-auto flex flex-column">
            {!readOnly && !studentsCanDraw && (
              <div className="flex items-center justify-center border-box p1 center bg-black-tint-20 text-black-tint-90 h6">
                <svg width="12" height="16">
                  <g fill="#e6e6e6">
                    <path d="M6 0C3.794 0 2 1.794 2 4v1.333H0V16h12V5.333h-2V4c0-2.206-1.794-4-4-4zM3.333 4A2.67 2.67 0 016 1.333 2.67 2.67 0 018.667 4v1.333H3.333V4zm7.334 2.667v8H1.333v-8h9.334z" />
                    <path d="M5.333 11.877v1.456h1.334v-1.456A1.997 1.997 0 008 10c0-1.103-.897-2-2-2s-2 .897-2 2c0 .868.56 1.601 1.333 1.877zM6 9.333a.668.668 0 010 1.334.668.668 0 010-1.334z" />
                  </g>
                </svg>
                <div>&nbsp; This resource is in read-only mode for students</div>
              </div>
            )}
            <iframe
              className="w-full"
              title={resource.title}
              src={resourceUrl}
              ref={iframeRef}
              style={{ height: iframeHeight }}
            />
          </div>
        </CenteredErrorBoundary>
      ) : (
        <LoadingDarkIcon className="absolute-center" data-testid="loading" />
      )}
    </div>
  );
}

export { MinervaResource };
